import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/supabase';
import { ProspectTitle, COMPANY_LICENSES } from '../../types';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Recruiter {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

const RecruiterProspectForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const recruiterToken = searchParams.get('recruiter_id');

  const [recruiter, setRecruiter] = useState<Recruiter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    title: 'Sr Mortgage Broker' as ProspectTitle,
    phone: '',
    email: '',
    nmls: '',
    expected_month: '',
    expected_start_date: '',
    state_licenses: [] as string[],
  });

  const [licenseDropdownOpen, setLicenseDropdownOpen] = useState(false);
  const licenseDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (licenseDropdownRef.current && !licenseDropdownRef.current.contains(event.target as Node)) {
        setLicenseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLicenseToggle = (state: string) => {
    setFormData((prev) => ({
      ...prev,
      state_licenses: prev.state_licenses.includes(state)
        ? prev.state_licenses.filter((s) => s !== state)
        : [...prev.state_licenses, state],
    }));
  };

  const handleRemoveLicense = (state: string) => {
    setFormData((prev) => ({
      ...prev,
      state_licenses: prev.state_licenses.filter((s) => s !== state),
    }));
  };

  useEffect(() => {
    if (recruiterToken) {
      validateToken();
    } else {
      setError('Missing recruiter ID. Please use a valid recruitment link.');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recruiterToken]);

  const validateToken = async () => {
    try {
      const data = await api.validateRecruiterToken(recruiterToken!);
      setRecruiter(data);
    } catch (err: any) {
      setError('Invalid or expired recruitment link. Please contact your administrator.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('First and last name are required');
      return false;
    }
    if (!formData.phone.trim() && !formData.email.trim()) {
      setError('Provide at least one contact method (phone or email)');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;
    if (!recruiterToken) return;

    setIsSubmitting(true);
    try {
      await api.createProspectFromRecruiter({
        recruiter_token: recruiterToken,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        title: formData.title,
        phone: formData.phone.trim() || undefined,
        email: formData.email.trim() || undefined,
        nmls: formData.nmls.trim() || undefined,
        expected_month: formData.expected_month || undefined,
        expected_start_date: formData.expected_start_date || undefined,
        state_licenses: formData.state_licenses.length > 0 ? formData.state_licenses : undefined,
      });
      setSubmitted(true);
    } catch {
      setError('Failed to submit prospect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      title: 'Sr Mortgage Broker',
      phone: '',
      email: '',
      nmls: '',
      expected_month: '',
      expected_start_date: '',
      state_licenses: [],
    });
    setSubmitted(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!recruiter) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Invalid Link</h1>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-emerald-500 text-5xl mb-4">&#10003;</div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Prospect Submitted!</h1>
          <p className="text-slate-500 mb-6">
            The prospect has been added to the system. The team will follow up shortly.
          </p>
          <button
            onClick={resetForm}
            className="px-6 py-2 bg-lumin-primary text-white rounded-2xl font-semibold"
          >
            Add Another Prospect
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/logo-lumin-lending.png"
            alt="Lumin Lending"
            className="h-12 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-slate-900">Add New Prospect</h1>
          <p className="text-slate-500 mt-1">
            Submitting as: <span className="font-semibold">{recruiter.first_name} {recruiter.last_name}</span>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-lg p-6 space-y-5">
          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700">Title</label>
            <select
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2"
            >
              <option value="Sr Mortgage Broker">Sr Mortgage Broker</option>
              <option value="Executive Branch Manager">Executive Branch Manager</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Personal Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john@example.com"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700">NMLS #</label>
            <input
              type="text"
              name="nmls"
              value={formData.nmls}
              onChange={handleInputChange}
              className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700">Expected Sponsor Date</label>
              <input
                type="date"
                name="expected_month"
                value={formData.expected_month}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Expected Start Date</label>
              <input
                type="date"
                name="expected_start_date"
                value={formData.expected_start_date}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2"
              />
              <p className="text-xs text-slate-400 mt-1">When they begin onboarding</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700">State Licenses</label>
            <div className="relative mt-1" ref={licenseDropdownRef}>
              <div
                onClick={() => setLicenseDropdownOpen(!licenseDropdownOpen)}
                className="min-h-[42px] w-full rounded-2xl border border-slate-200 px-3 py-2 cursor-pointer flex items-center justify-between gap-2 bg-white"
              >
                <div className="flex flex-wrap gap-1 flex-1">
                  {formData.state_licenses.length === 0 ? (
                    <span className="text-slate-400">Select states...</span>
                  ) : (
                    formData.state_licenses.map((state) => (
                      <span
                        key={state}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lumin-primary/10 text-lumin-primary text-xs font-medium"
                      >
                        {state}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveLicense(state);
                          }}
                          className="hover:bg-lumin-primary/20 rounded-full p-0.5"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  )}
                </div>
                <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${licenseDropdownOpen ? 'rotate-180' : ''}`} />
              </div>
              {licenseDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-2xl shadow-lg max-h-48 overflow-y-auto">
                  {COMPANY_LICENSES.map((state) => (
                    <div
                      key={state}
                      onClick={() => handleLicenseToggle(state)}
                      className={`px-3 py-2 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${
                        formData.state_licenses.includes(state) ? 'bg-lumin-primary/5' : ''
                      }`}
                    >
                      <span className="text-sm text-slate-700">{state}</span>
                      {formData.state_licenses.includes(state) && (
                        <span className="text-lumin-primary text-sm">✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {formData.state_licenses.length} state{formData.state_licenses.length !== 1 ? 's' : ''} selected
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 bg-lumin-primary text-white rounded-2xl font-semibold disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Prospect'}
          </button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">
          Need help? Contact your administrator.
        </p>
      </div>
    </div>
  );
};

export default RecruiterProspectForm;
